/**
 * userTasks — designer-created tasks persisted per project.
 *
 * Materialise as typed :Alert nodes with role="task" so the cockpit's
 * existing alert + tracker-tasks plumbing reads them without a new
 * kind. mergeProjectLayers picks them up and they show up everywhere
 * an alert would.
 */
import type { GraphNode } from "../types/ontology";

const KEY_PREFIX = "foyrmind.userTasks.";

export interface UserTask {
  readonly id: string;
  readonly title: string;
  readonly state: "open" | "doing" | "done";
  readonly createdAt: string;
  /** Optional dueOn typed instant (xsd:dateTime). */
  readonly dueOn?: string;
  /** Free-text owner / assignee. */
  readonly owner?: string;
  /** Free-text source — e.g. "chat:rohan-saxena". */
  readonly source: string;
}

function key(projectId: string): string { return KEY_PREFIX + projectId; }

export function readUserTasks(projectId: string): ReadonlyArray<UserTask> {
  try {
    const raw = window.localStorage.getItem(key(projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is UserTask =>
      Boolean(t) && typeof t === "object"
      && typeof (t as UserTask).id === "string"
      && typeof (t as UserTask).title === "string",
    );
  } catch (err) {
    console.error("[userTasks] failed to read", err);
    return [];
  }
}

export function addUserTask(projectId: string, input: { title: string; owner?: string; dueOn?: string; source?: string }): UserTask {
  const task: UserTask = {
    id: `task-${projectId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: input.title.trim(),
    state: "open",
    createdAt: new Date().toISOString(),
    ...(input.dueOn ? { dueOn: input.dueOn } : {}),
    ...(input.owner ? { owner: input.owner } : {}),
    source: input.source ?? "user",
  };
  try {
    const all = [...readUserTasks(projectId), task];
    window.localStorage.setItem(key(projectId), JSON.stringify(all));
  } catch (err) {
    console.error("[userTasks] failed to persist", err);
  }
  return task;
}

export function setUserTaskState(projectId: string, taskId: string, state: UserTask["state"]): void {
  try {
    const all = readUserTasks(projectId).map((t) => t.id === taskId ? { ...t, state } : t);
    window.localStorage.setItem(key(projectId), JSON.stringify(all));
  } catch (err) {
    console.error("[userTasks] failed to set state", err);
  }
}

export function removeUserTask(projectId: string, taskId: string): void {
  try {
    const all = readUserTasks(projectId).filter((t) => t.id !== taskId);
    window.localStorage.setItem(key(projectId), JSON.stringify(all));
  } catch (err) {
    console.error("[userTasks] failed to remove", err);
  }
}

/** Project the user tasks as typed :Alert graph nodes with role="task". */
export function asGraphNodes(projectId: string): ReadonlyArray<GraphNode> {
  return readUserTasks(projectId).map<GraphNode>((t) => ({
    id: t.id,
    kind: "Alert",
    label: t.title,
    attrs: {
      role: "task",
      state: t.state,
      atTime: t.createdAt,
      source: t.source,
      ...(t.dueOn ? { dueOn: t.dueOn } : {}),
      ...(t.owner ? { owner: t.owner } : {}),
    },
  }));
}
